import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Youtube } from "lucide-react";
import { Reveal } from "./Reveal";

function fmtViews(v) {
  const n = parseInt(v, 10);
  if (isNaN(n)) return "";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M views`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K views`;
  return `${n} views`;
}

export default function YouTubeSection({ videos = [], channelUrl, instagramEmbeds = [], instagramReel }) {
  const [active, setActive] = useState(null);
  if (!videos.length) return null;

  const embeds = (instagramEmbeds && instagramEmbeds.length ? instagramEmbeds : instagramReel ? [instagramReel] : [])
    .map((u) => u.replace(/\/+$/, "") + "/embed");

  return (
    <section id="videos" className="relative bg-obsidian py-24 md:py-36 border-b border-white/10" data-testid="youtube-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div>
            <p className="overline text-gold mb-6">From The Channel</p>
            <h2 className="headline text-5xl md:text-7xl text-white">Videos</h2>
          </div>
          <a
            href={channelUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="youtube-subscribe"
            className="group inline-flex items-center gap-3 bg-[#4FB04A] text-black px-8 py-4 rounded-full font-display uppercase tracking-[0.18em] text-sm shadow-[0_12px_34px_-8px_rgba(79,176,74,0.65)] hover:bg-[#3f9e43] hover:scale-105 transition-all duration-300 w-fit"
          >
            <Youtube size={20} className="group-hover:scale-110 transition-transform" /> Subscribe on YouTube
          </a>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {videos.slice(0, 4).map((v, i) => (
            <motion.button
              key={v.video_id}
              onClick={() => setActive(v)}
              data-testid={`video-item-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 4) * 0.07, ease: [0.16, 1, 0.3, 1] }}
              className="group text-left"
            >
              <div className="relative aspect-video overflow-hidden mb-4">
                <img
                  src={`https://i.ytimg.com/vi/${v.video_id}/hqdefault.jpg`}
                  alt={v.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/55 transition-colors" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="w-14 h-14 rounded-full bg-[#FF0000]/90 flex items-center justify-center scale-90 group-hover:scale-100 transition-transform">
                    <Play size={22} className="text-white ml-1" />
                  </span>
                </div>
              </div>
              <h3 className="font-body text-white/90 text-base leading-snug mb-2 line-clamp-2 group-hover:text-gold transition-colors">
                {v.title}
              </h3>
              <p className="font-display uppercase tracking-[0.12em] text-[0.66rem] text-white/45">
                {fmtViews(v.views)}{v.views && v.date ? " · " : ""}{v.date}
              </p>
            </motion.button>
          ))}
        </div>

        {embeds.length > 0 && (
          <Reveal className="mt-16 md:mt-20 text-center" delay={0.1}>
            <p className="overline text-gold mb-6">Straight From Instagram</p>
            <div className="flex flex-wrap justify-center gap-6 max-w-4xl mx-auto">
              {embeds.map((src, i) => (
                <div
                  key={src}
                  className="relative w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)] overflow-hidden rounded-lg border border-white/10 bg-black"
                  style={{ aspectRatio: "9 / 14" }}
                >
                  <iframe
                    title={`instagram-${i}`}
                    src={src}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    scrolling="no"
                    allowtransparency="true"
                    allow="encrypted-media"
                    data-testid={`instagram-embed-${i}`}
                  />
                </div>
              ))}
            </div>
          </Reveal>
        )}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-md flex items-center justify-center p-6"
            onClick={() => setActive(null)}
            data-testid="video-modal"
          >
            <motion.div
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 20 }}
              transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.5 }}
              className="relative w-full max-w-4xl aspect-video"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setActive(null)}
                data-testid="video-modal-close"
                className="absolute -top-4 -right-4 z-10 bg-gold text-black w-9 h-9 flex items-center justify-center hover:rotate-90 transition-transform"
              >
                <X size={18} />
              </button>
              <iframe
                title={active.title}
                src={`https://www.youtube.com/embed/${active.video_id}?autoplay=1`}
                className="w-full h-full border border-white/10"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
